// OSIS - Planificator Concedii - Adaptat pentru echipa OSIS (11 angajați)
import React, { useState } from 'react';
import { 
  Calendar, User, Clock, CheckCircle, XCircle, Plus, MapPin, 
  Plane, Sun, Coffee, Factory, Users, AlertTriangle, TrendingUp 
} from 'lucide-react';
import Card from '../../components/Card';
import Table from '../../components/Table';
import Badge from '../../components/Badge';
import Modal from '../../components/Modal';
import { mockData, getStatusColor } from '../../utils/mockData';

const PlanificatorConcedii = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [newVacationForm, setNewVacationForm] = useState({
    employeeId: 1,
    startDate: '',
    endDate: '',
    type: 'Concediu de odihnă',
    reason: '',
    notes: ''
  });

  // Concedii extins pentru toți angajații OSIS
  const osisVacations = [
    ...mockData.vacations,
    // Concedii suplimentare pentru ceilalți angajați
    {
      id: 4,
      employee: 'Filip Paul - Ionel',
      startDate: '2024-06-10',
      endDate: '2024-06-21',
      days: 12,
      status: 'Planificat',
      type: 'Concediu de odihnă',
      requestDate: '2024-03-05',
      approvedBy: 'Ciprian Aostăcioaei',
      reason: 'Concediu de vară cu familia'
    },
    {
      id: 5,
      employee: 'Ungureanu Ștefan',
      startDate: '2024-08-05',
      endDate: '2024-08-16',
      days: 12,
      status: 'Planificat',
      type: 'Concediu de odihnă',
      requestDate: '2024-04-15',
      approvedBy: 'Ciprian Aostăcioaei',
      reason: 'Concediu planificat vară'
    },
    {
      id: 6,
      employee: 'Murariu Iulian - Eusebiu',
      startDate: '2024-05-01',
      endDate: '2024-05-03',
      days: 3,
      status: 'Aprobat',
      type: 'Sărbători legale',
      requestDate: '2024-04-10',
      approvedBy: 'Ciprian Aostăcioaei',
      reason: '1 Mai și ziua de 2 Mai'
    },
    {
      id: 7,
      employee: 'Simion Tiberiu',
      startDate: '2024-07-22',
      endDate: '2024-08-02',
      days: 12,
      status: 'Planificat',
      type: 'Concediu de odihnă',
      requestDate: '2024-05-01',
      approvedBy: 'Ciprian Aostăcioaei',
      reason: 'Concediu cu familia la mare'
    }
  ];

  const handleSubmitVacation = () => {
    const selectedEmployee = mockData.employees.find(e => e.id === parseInt(newVacationForm.employeeId));
    const newVacation = {
      id: osisVacations.length + 1,
      employee: selectedEmployee?.name || '',
      startDate: newVacationForm.startDate,
      endDate: newVacationForm.endDate,
      days: calculateDays(),
      status: 'În aprobare',
      type: newVacationForm.type,
      reason: newVacationForm.reason,
      requestDate: new Date().toISOString().split('T')[0],
      approvedBy: 'În așteptare',
      notes: newVacationForm.notes
    };

    console.log('New OSIS vacation request:', newVacation);
    setIsModalOpen(false);
    
    // Reset form
    setNewVacationForm({
      employeeId: 1,
      startDate: '',
      endDate: '',
      type: 'Concediu de odihnă',
      reason: '',
      notes: ''
    });
  };

  const calculateDays = () => {
    if (newVacationForm.startDate && newVacationForm.endDate) {
      const start = new Date(newVacationForm.startDate);
      const end = new Date(newVacationForm.endDate);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      return diffDays;
    }
    return 0;
  };

  // Statistici concedii pentru OSIS
  const vacationStats = {
    totalRequests: osisVacations.length,
    approved: osisVacations.filter(v => v.status === 'Aprobat' || v.status === 'Finalizat').length,
    pending: osisVacations.filter(v => v.status === 'Planificat' || v.status === 'În aprobare').length,
    current: osisVacations.filter(v => v.status === 'În curs').length
  };

  // Disponibilitatea echipei pe luni
  const getMonthAvailability = () => {
    const currentMonth = new Date(selectedYear, selectedMonth);
    const vacationsThisMonth = osisVacations.filter(v => {
      const startDate = new Date(v.startDate);
      const endDate = new Date(v.endDate);
      return (startDate.getMonth() === selectedMonth && startDate.getFullYear() === selectedYear) ||
             (endDate.getMonth() === selectedMonth && endDate.getFullYear() === selectedYear) ||
             (startDate <= currentMonth && endDate >= currentMonth);
    });

    const totalEmployees = mockData.employees.length;
    const employeesOnVacation = vacationsThisMonth.length;
    const availableEmployees = totalEmployees - employeesOnVacation;

    return {
      total: totalEmployees,
      onVacation: employeesOnVacation,
      available: availableEmployees,
      percentage: Math.round((availableEmployees / totalEmployees) * 100)
    };
  };

  const availability = getMonthAvailability();

  const tableColumns = [
    {
      header: 'Angajat',
      accessor: 'employee',
      cell: (row) => (
        <div className="flex items-center space-x-2">
          <User className="w-4 h-4 text-gray-400" />
          <div>
            <p className="font-medium text-gray-900 dark:text-white">{row.employee}</p>
            <p className="text-xs text-gray-500">
              {mockData.employees.find(e => e.name === row.employee)?.department || 'N/A'}
            </p>
          </div>
        </div>
      ),
    },
    {
      header: 'Tip Concediu',
      accessor: 'type',
      cell: (row) => (
        <div className="flex items-center space-x-2">
          {row.type === 'Concediu de odihnă' && <Sun className="w-4 h-4 text-yellow-500" />}
          {row.type === 'Concediu medical' && <Plus className="w-4 h-4 text-red-500" />}
          {row.type === 'Concediu creștere copil' && <User className="w-4 h-4 text-pink-500" />}
          {row.type === 'Zi liberă' && <Coffee className="w-4 h-4 text-blue-500" />}
          <span className="text-sm">{row.type}</span>
        </div>
      ),
    },
    {
      header: 'Perioada',
      accessor: 'period',
      cell: (row) => (
        <div>
          <p className="text-sm font-medium">
            {new Date(row.startDate).toLocaleDateString('ro-RO')} - 
            {new Date(row.endDate).toLocaleDateString('ro-RO')}
          </p>
          <p className="text-xs text-gray-500">{row.days} zile</p>
        </div>
      ),
    },
    {
      header: 'Status',
      accessor: 'status',
      cell: (row) => <Badge variant={getStatusColor(row.status)}>{row.status}</Badge>,
    },
    {
      header: 'Aprobat de',
      accessor: 'approvedBy',
      cell: (row) => (
        <div>
          <p className="text-sm font-medium">{row.approvedBy}</p>
          <p className="text-xs text-gray-500">
            Cerere: {new Date(row.requestDate).toLocaleDateString('ro-RO')}
          </p>
        </div>
      ),
    },
  ];

  const monthNames = [
    'Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie',
    'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie'
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <Factory className="w-8 h-8 text-primary-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Planificator Concedii OSIS
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Gestionare concedii pentru echipa de producție și instalare pardoseli
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <select 
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
            className="form-input"
          >
            {monthNames.map((month, index) => (
              <option key={index} value={index}>{month}</option>
            ))}
          </select>
          
          <select 
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="form-input"
          >
            <option value="2024">2024</option>
            <option value="2025">2025</option>
          </select>
          
          <button
            onClick={() => setIsModalOpen(true)}
            className="btn-primary flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Cerere Concediu
          </button>
        </div>
      </div>

      {/* Statistici Rapide */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Cereri</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{vacationStats.totalRequests}</p>
              <p className="text-sm text-gray-500">În 2024</p>
            </div>
            <Calendar className="w-8 h-8 text-primary-600" />
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Aprobate</p>
              <p className="text-2xl font-bold text-green-600">{vacationStats.approved}</p>
              <p className="text-sm text-gray-500">Finalizate/programate</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">În Așteptare</p>
              <p className="text-2xl font-bold text-yellow-600">{vacationStats.pending}</p>
              <p className="text-sm text-gray-500">Pentru aprobare</p>
            </div>
            <Clock className="w-8 h-8 text-yellow-600" />
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">În Concediu</p>
              <p className="text-2xl font-bold text-blue-600">{vacationStats.current}</p>
              <p className="text-sm text-gray-500">Acum activ</p>
            </div>
            <Plane className="w-8 h-8 text-blue-600" />
          </div>
        </Card>
      </div>

      {/* Disponibilitatea Echipei */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title={`Disponibilitatea Echipei - ${monthNames[selectedMonth]} ${selectedYear}`} icon={Users}>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-600 dark:text-gray-400">Angajați totali</span>
              <span className="font-semibold">{availability.total}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600 dark:text-gray-400">În concediu</span>
              <span className="font-semibold text-yellow-600">{availability.onVacation}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600 dark:text-gray-400">Disponibili</span>
              <span className="font-semibold text-green-600">{availability.available}</span>
            </div>
            
            <div className="pt-4 border-t">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">Disponibilitate echipă</span>
                <span className="text-sm font-semibold">{availability.percentage}%</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                <div 
                  className={`h-3 rounded-full transition-all duration-300 ${
                    availability.percentage >= 80 ? 'bg-green-500' :
                    availability.percentage >= 60 ? 'bg-yellow-500' :
                    'bg-red-500'
                  }`}
                  style={{ width: `${availability.percentage}%` }}
                ></div>
              </div>
            </div>

            {availability.percentage < 70 && (
              <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                    Atenție: Disponibilitate redusă
                  </p>
                  <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                    Ar trebui să reprogramați concediile pentru a asigura continuitatea producției.
                  </p>
                </div>
              </div>
            )}
          </div>
        </Card>

        <Card title="Echipele Critice" icon={Factory}>
          <div className="space-y-4">
            <div className="p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-primary-800 dark:text-primary-200">Echipa Producție</span>
                <span className="text-sm text-primary-600 dark:text-primary-400">4 angajați</span>
              </div>
              <div className="text-sm text-primary-700 dark:text-primary-300">
                Daniel (Șef), Beniamin, Ionuț, Romică - Esențiali pentru fabricație
              </div>
            </div>

            <div className="p-4 bg-secondary-50 dark:bg-secondary-900/20 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-secondary-800 dark:text-secondary-200">Echipa Montaj</span>
                <span className="text-sm text-secondary-600 dark:text-secondary-400">3 angajați</span>
              </div>
              <div className="text-sm text-secondary-700 dark:text-secondary-300">
                Ștefan, Eusebiu, Beniamin - Instalări la clienți
              </div>
            </div>

            <div className="p-4 bg-accent-blue/10 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-blue-800 dark:text-blue-200">Management/Tehnică</span>
                <span className="text-sm text-blue-600 dark:text-blue-400">3 angajați</span>
              </div>
              <div className="text-sm text-blue-700 dark:text-blue-300">
                Ciprian, Ionel, Tiberiu - Funcții critice administrare
              </div>
            </div>

            <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <p className="text-xs text-gray-600 dark:text-gray-400">
                <strong>Recomandare:</strong> Evitați suprapunerea concediilor în aceeași echipă.
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Tabel Concedii */}
      <Card title="Toate Concediile OSIS" icon={Calendar}>
        <div className="overflow-x-auto">
          <Table 
            columns={tableColumns} 
            data={osisVacations}
            className="min-w-full"
          />
        </div>
      </Card>

      {/* Modal Cerere Concediu */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Cerere Concediu Nouă - OSIS"
        size="lg"
      >
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Angajat OSIS</label>
              <select 
                value={newVacationForm.employeeId}
                onChange={(e) => setNewVacationForm({...newVacationForm, employeeId: e.target.value})}
                className="form-input"
              >
                {mockData.employees.map(emp => (
                  <option key={emp.id} value={emp.id}>
                    {emp.name} - {emp.department}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Tipul concediului</label>
              <select 
                value={newVacationForm.type}
                onChange={(e) => setNewVacationForm({...newVacationForm, type: e.target.value})}
                className="form-input"
              >
                <option>Concediu de odihnă</option>
                <option>Concediu medical</option>
                <option>Zi liberă</option>
                <option>Concediu fără plată</option>
                <option>Concediu creștere copil</option>
              </select>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Data început</label>
              <input 
                type="date" 
                value={newVacationForm.startDate}
                onChange={(e) => setNewVacationForm({...newVacationForm, startDate: e.target.value})}
                className="form-input" 
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Data sfârșit</label>
              <input 
                type="date" 
                value={newVacationForm.endDate}
                onChange={(e) => setNewVacationForm({...newVacationForm, endDate: e.target.value})}
                className="form-input" 
              />
            </div>
          </div>

          {calculateDays() > 0 && (
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                <strong>Durată:</strong> {calculateDays()} zile lucrătoare
              </p>
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium mb-2">Motivul cererii</label>
            <textarea 
              value={newVacationForm.reason}
              onChange={(e) => setNewVacationForm({...newVacationForm, reason: e.target.value})}
              className="form-input"
              rows="3"
              placeholder="Descrieți motivul pentru care solicitați concediul..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Observații (opțional)</label>
            <textarea 
              value={newVacationForm.notes}
              onChange={(e) => setNewVacationForm({...newVacationForm, notes: e.target.value})}
              className="form-input"
              rows="2"
              placeholder="Observații suplimentare pentru management..."
            />
          </div>

          {/* Info despre impact echipă */}
          <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                  Impact asupra echipei
                </p>
                <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                  Managementul va analiza impactul asupra capacității de producție și programului de instalări.
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              onClick={() => setIsModalOpen(false)}
              className="btn-secondary"
            >
              Anulează
            </button>
            <button
              onClick={handleSubmitVacation}
              disabled={!newVacationForm.startDate || !newVacationForm.endDate || !newVacationForm.reason}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Trimite Cererea
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PlanificatorConcedii;